// <purpose>Find a quoted sentence inside rendered text (compare mode's marks, 2026-10-30): a DOM
// Range over the first occurrence of `quote` under `root`, matching across element boundaries and
// with every run of whitespace read as one space — the served quote is the mail's or the letter's
// own words, but the rendered DOM splits them over tags and line breaks.</purpose>
// <dependencies>DOM only.</dependencies>
// <constraints>Read-only: it never mutates the DOM it searches, because the letter column is
// React-owned (wrapping a text node would break React's own references). Callers draw the mark as a
// layer from the range's client rects instead. Text inside `[data-mark-layer]` is skipped.</constraints>

const WS = /\s/;

/** Whitespace-collapsed, trimmed, with typographic quotes and dashes read as their plain forms. */
export const normalizeQuote = (s: string): string =>
  s.replace(/[„“”]/g, '"').replace(/[‚‘’]/g, "'").replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim();

const plainChar = (c: string): string =>
  c === '„' || c === '“' || c === '”' ? '"' : c === '‚' || c === '‘' || c === '’' ? "'" : c === '–' || c === '—' ? '-' : c;

export function findTextRange(root: Node, quote: string | null | undefined): Range | null {
  if (!quote) return null;
  const needle = normalizeQuote(quote);
  if (!needle) return null;
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => ((n.parentElement?.closest('[data-mark-layer]')) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  let flat = '';
  const map: { node: Text; offset: number }[] = [];
  let lastSpace = true;
  for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
    const t = n.data;
    for (let i = 0; i < t.length; i++) {
      const c = t[i];
      if (WS.test(c)) {
        if (lastSpace) continue;
        flat += ' '; lastSpace = true;
      } else {
        flat += plainChar(c); lastSpace = false;
      }
      map.push({ node: n, offset: i });
    }
  }
  let at = flat.indexOf(needle);
  if (at < 0) at = flat.toLowerCase().indexOf(needle.toLowerCase());
  if (at < 0) return null;
  const start = map[at];
  const end = map[at + needle.length - 1];
  const range = doc.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset + 1);
  return range;
}

/** Select the quote inside a textarea (Bearbeiten opened on a marked sentence). */
export function selectInTextarea(el: HTMLTextAreaElement, quote: string): boolean {
  const needle = normalizeQuote(quote);
  if (!needle) return false;
  // Same collapse as above, with an index map back into the raw value.
  let flat = '';
  const map: number[] = [];
  let lastSpace = true;
  const v = el.value;
  for (let i = 0; i < v.length; i++) {
    const c = v[i];
    if (WS.test(c)) { if (lastSpace) continue; flat += ' '; lastSpace = true; } else { flat += plainChar(c); lastSpace = false; }
    map.push(i);
  }
  const at = flat.indexOf(needle);
  if (at < 0) return false;
  el.setSelectionRange(map[at], map[at + needle.length - 1] + 1);
  return true;
}
